import type { FileRouteTypes } from '@client/routeTree.gen';
import { isNetworkError } from '@client/utils/errors/network-error-detection';
import { TRPCClientError } from '@trpc/client';

export const SESSION_EXPIRED_TRPC_RESULT = {
    error: {
        message: 'Session expired',
        code: -32001,
        data: {
            code: 'UNAUTHORIZED',
            httpStatus: 401,
        },
    },
} as const;

export const SYNTHETIC_SESSION_EXPIRED_MARKER = '__curiumSyntheticSessionExpired__';

function isRecord(value: unknown): value is Record<PropertyKey, unknown> {
    return value !== null && typeof value === 'object';
}

function hasOwn<K extends PropertyKey>(value: unknown, key: K): value is Record<K, unknown> {
    return isRecord(value) && Object.hasOwn(value, key);
}

export function buildSessionExpiredError() {
    const error = new TRPCClientError(SESSION_EXPIRED_TRPC_RESULT.error.message, {
        result: SESSION_EXPIRED_TRPC_RESULT,
    });
    Object.defineProperty(error, SYNTHETIC_SESSION_EXPIRED_MARKER, {
        value: true,
        enumerable: false,
    });
    return error;
}

export function isSyntheticSessionExpiredError(error: unknown): boolean {
    return hasOwn(error, SYNTHETIC_SESSION_EXPIRED_MARKER) && error[SYNTHETIC_SESSION_EXPIRED_MARKER] === true;
}

export function isTrpcExecutionDeadlineError(error: unknown): boolean {
    return hasOwn(error, 'data') && isRecord(error.data) && error.data.errorId === 'request.executionDeadline';
}

export function isUnauthorizedTrpcError(error: unknown): error is { data: { code: 'UNAUTHORIZED' } } {
    return (
        hasOwn(error, 'data') &&
        isRecord(error.data) &&
        hasOwn(error.data, 'code') &&
        error.data.code === 'UNAUTHORIZED'
    );
}

export function isPublicRoute(pathname: string): boolean {
    return pathname === '/public' || pathname.startsWith('/public/');
}

// Only routes under these prefixes are considered authenticated areas where the
// expired-session guard should block tRPC traffic. Everything else — anonymous
// flows like /password-reset/*, /invite, Auth0 transitions, and
// informational pages — must be free to make unauthenticated tRPC calls even
// when a stale authTokenExpired flag is persisted in localStorage from a prior
// signed-in session.
//
// Each entry must be a registered route path (from the generated TanStack
// registry) so renaming or removing a route surfaces here as a type error
// instead of silent drift. `/console` has no standalone route — its children
// all live under `/console/$orgId` — so we pin against that canonical child.
type ProtectedPrefix =
    | FileRouteTypes['to']
    | ([Extract<FileRouteTypes['to'], `/console/${string}`>] extends [never] ? never : '/console');
const PROTECTED_ROUTE_PREFIXES = [
    '/console',
    '/admin-console',
    '/attestation',
    '/terms-acceptance',
] as const satisfies readonly ProtectedPrefix[];

export function isProtectedRoute(pathname: string): boolean {
    return PROTECTED_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * Public flows must stay anonymous even when the browser currently has a valid
 * session token. Attaching a bearer token to `/public/*` requests can turn
 * otherwise-public tRPC procedures into org-scoped auth failures.
 */
export function shouldAttachAuthHeader({ hasToken, pathname }: { hasToken: boolean; pathname: string }) {
    return hasToken && !isPublicRoute(pathname);
}

/**
 * Unexpected unauthorized responses should only trigger session-expiry handling
 * inside authenticated sections. Anonymous and public routes should surface the
 * error locally instead of redirecting the user into the auth flow.
 */
export function shouldExpireSessionOnUnauthorized(pathname: string): boolean {
    return isProtectedRoute(pathname);
}

export function shouldEnableTrpcQuery({
    authTokenExpired,
    isProtectedRoute: onProtectedRoute,
}: {
    authTokenExpired: boolean;
    isProtectedRoute: boolean;
}) {
    if (!onProtectedRoute) {
        return true;
    }
    return !authTokenExpired;
}

/**
 * Public routes must continue anonymously when localStorage still contains an expired token
 * from a prior signed-in session. Clearing the stale token avoids attaching a Bearer header
 * to otherwise anonymous requests without tripping the protected-route expired-session flow.
 */
export function shouldClearExpiredTokenOnPublicRoute({
    hasToken,
    isExpired,
    isProtectedRoute: onProtectedRoute,
}: {
    hasToken: boolean;
    isExpired: boolean;
    isProtectedRoute: boolean;
}) {
    return !onProtectedRoute && hasToken && isExpired;
}

/**
 * Determines whether an outgoing tRPC operation must be short-circuited due to an expired session.
 *
 * Returns true while either the current token is present and expired, OR the session has already
 * been flagged as expired. The latter case is critical: once `clearAuthToken()` wipes the token,
 * subsequent protected operations during the redirect window would otherwise reach the API
 * unauthenticated.
 *
 * Only applied to protected routes — anonymous public routes must continue to run their
 * unauthenticated tRPC calls even when a stale expired-session flag is persisted in localStorage.
 */
export function shouldBlockExpiredRequest({
    hasToken,
    isExpired,
    authTokenExpired,
    isProtectedRoute: onProtectedRoute,
}: {
    hasToken: boolean;
    isExpired: boolean;
    authTokenExpired: boolean;
    isProtectedRoute: boolean;
}) {
    if (!onProtectedRoute) {
        return false;
    }
    return (hasToken && isExpired) || authTokenExpired;
}

/**
 * Determines whether an outgoing tRPC operation must be short-circuited because a deliberate
 * logout is under way.
 *
 * `logout()` clears the token and keeps `authTokenExpired` false (so no expiry modal shows),
 * then waits on the Auth0 redirect. The page stays mounted for that window, and a
 * refetch-on-focus or refetch-on-reconnect would otherwise reach the API without a bearer
 * token (CUR-2010). Same scope as `shouldBlockExpiredRequest`: protected routes only.
 */
export function shouldBlockLoggingOutRequest({
    isLoggingOut,
    isProtectedRoute: onProtectedRoute,
}: {
    isLoggingOut: boolean;
    isProtectedRoute: boolean;
}) {
    return onProtectedRoute && isLoggingOut;
}

/** The sign-in route `RequireAuth` sends an expired or unrestorable session to. */
const LOGIN_ROUTE = '/login' satisfies FileRouteTypes['to'];

export function isLoginRoute(pathname: string): boolean {
    return pathname === LOGIN_ROUTE || pathname.startsWith(`${LOGIN_ROUTE}/`);
}

/**
 * Determines whether an outgoing tRPC operation must be short-circuited because `RequireAuth`
 * is sending the tab to /login after a session failure.
 *
 * The SPA navigation changes `window.location.pathname` to /login before the protected page
 * unmounts (the login route is a lazy chunk), so for that window the page's query observers are
 * live while every pathname-scoped guard treats the tab as an anonymous route. A
 * refetch-on-focus or a store-driven re-render would otherwise reach the API without a bearer
 * token (CUR-2473). Scoped to the routes that window spans: the protected route the redirect
 * began on and /login itself. Public and other anonymous routes keep their own rules.
 */
export function shouldBlockLoginRedirectRequest({
    isRedirectingToLogin,
    pathname,
}: {
    isRedirectingToLogin: boolean;
    pathname: string;
}) {
    return isRedirectingToLogin && (isProtectedRoute(pathname) || isLoginRoute(pathname));
}

/**
 * The session's hard stop on a query retry: an expired session on a protected route. A query with
 * a retry policy of its own applies it before its own rule.
 */
export function sessionStopsQueryRetry({
    authTokenExpired,
    isProtectedRoute: onProtectedRoute,
}: {
    authTokenExpired: boolean;
    isProtectedRoute: boolean;
}) {
    return authTokenExpired && onProtectedRoute;
}

export function shouldRetryTrpcQuery({
    authTokenExpired,
    isProtectedRoute: onProtectedRoute,
    failureCount,
    error,
}: {
    authTokenExpired: boolean;
    isProtectedRoute: boolean;
    failureCount: number;
    error: unknown;
}) {
    if (sessionStopsQueryRetry({ authTokenExpired, isProtectedRoute: onProtectedRoute })) {
        return false;
    }

    if (isNetworkError(error)) {
        return failureCount < 3;
    }

    return false;
}

/**
 * The session's hard stops on a mutation retry: guards 1 and 2 of `shouldRetryTrpcMutation`. A
 * mutation with a retry policy of its own applies them before its own rule.
 */
export function sessionStopsMutationRetry({ authTokenExpired, error }: { authTokenExpired: boolean; error: unknown }) {
    return isSyntheticSessionExpiredError(error) || authTokenExpired;
}

/**
 * Decides whether a tRPC mutation should be retried after a failure.
 *
 * Two guards:
 * 1. Synthetic session-expired errors from `authTokenCheckLink` must never be retried.
 * 2. Any retry while `authTokenExpired` is true is blocked unconditionally. Mutation retryers
 *    outlive the originating component: if a protected-page mutation first fails with a network
 *    error and the session then expires mid-air, `RequireAuth` navigates to `/login` and the
 *    pathname at retry time is no longer protected. Relying solely on the synthetic marker or
 *    the current pathname would let that retry slip through `authTokenCheckLink` and re-fire
 *    unauthenticated against the API. The hard stop closes that race. The cost — skipping
 *    retries for legitimate mutations on anonymous public routes that happen to have a stale
 *    expired flag in localStorage — is acceptable: the caller can retry manually, and the
 *    alternative would require persisting origin classification per mutation.
 */
export function shouldRetryTrpcMutation({
    authTokenExpired,
    failureCount,
    error,
}: {
    authTokenExpired: boolean;
    failureCount: number;
    error: unknown;
}) {
    // A deadline does not roll back work. Retrying may duplicate a write that
    // is still executing on the server, so this discriminator is a hard stop.
    if (isTrpcExecutionDeadlineError(error)) {
        return false;
    }

    if (sessionStopsMutationRetry({ authTokenExpired, error })) {
        return false;
    }

    if (isNetworkError(error) && failureCount < 1) {
        return true;
    }

    return false;
}
